import React from 'react';
import PropTypes from 'prop-types';

import * as Sentry from '@sentry/browser';

import { useTranslation } from 'react-i18next';
import { Icon, Card, TitleCard, SubTitleCard, Button, Form, Message, ToastContext } from '@weezevent/nacre';
import { InfoTextTooltip } from '../../attendee/detail/components/infotext-tooltip/infoTextTooltip';
import InvoiceModel from '../../../models/invoiceModel';
import Style from './billing.module.css';
import { useForm } from '../../../utils/validators';
import useAppContext from '../../../context/useAppContext';
import { useSegment } from '../../../components/tracking/trackingProvider';

function BillingHeader({ title = null, subtitle = null, onEdit = () => {}, displayEdit = false }) {
    const { t } = useTranslation();
    return (
        <div className={Style['header']}>
            <div className={Style['title-container']}>
                {title && <TitleCard title={title} />}
                {subtitle && <SubTitleCard title={subtitle} />}
            </div>
            {displayEdit && (
                <div className={Style['edit']} onClick={onEdit}>
                    <span className={Style['edit-label']}>{t('ticket.common.action.edit')}</span>
                    <Icon name="edit" className={Style['edit-icon']} />
                </div>
            )}
        </div>
    );
}

BillingHeader.propTypes = {
    title: PropTypes.node,
    subtitle: PropTypes.node,
    onEdit: PropTypes.func,
    displayEdit: PropTypes.bool
};

function FormBilling({ form = null, isSubmitting = false, onChangeForm = () => {}, onSubmit = () => {}, onCancel = () => {} }) {
    const { t } = useTranslation();

    if (!form) {
        return null;
    }

    return (
        <>
            <Message type="default" title={<span className={Style['message__info-optional']}>{t('ticket.manager.orders.invoice.messageOptional')}</span>} />
            <Form>
                <Form.Field>
                    <Form.Label label={t('ticket.manager.orders.invoice.label.company')} />
                    <Form.Input name="company" disabled={isSubmitting} onChange={v => onChangeForm('company', v.target.value)} value={form.company} />
                </Form.Field>
                <Form.Field>
                    <Form.Label label={t('ticket.manager.orders.invoice.label.vat_number')} />
                    <Form.Input name="vat_number" disabled={isSubmitting} onChange={v => onChangeForm('vat_number', v.target.value)} value={form.vat_number} />
                </Form.Field>
                <Form.Field>
                    <Form.Label label={t('ticket.manager.orders.invoice.label.address')} />
                    <Form.Input name="address" disabled={isSubmitting} onChange={v => onChangeForm('address', v.target.value)} value={form.address} />
                </Form.Field>
                <Form.Field>
                    <Form.Label label={t('ticket.manager.orders.invoice.label.city')} />
                    <Form.Input name="city" disabled={isSubmitting} onChange={v => onChangeForm('city', v.target.value)} value={form.city} />
                </Form.Field>
                <Form.Field>
                    <Form.Label label={t('ticket.manager.orders.invoice.label.zip_code')} />
                    <Form.Input name="zip_code" disabled={isSubmitting} onChange={v => onChangeForm('zip_code', v.target.value)} value={form.zip_code} />
                </Form.Field>
                <Form.Field>
                    <Form.Label label={t('ticket.manager.orders.invoice.label.country')} />
                    <Form.Input name="country" disabled={isSubmitting} onChange={v => onChangeForm('country', v.target.value)} value={form.country} />
                </Form.Field>
                <Form.Field>
                    <Form.Label label={t('ticket.manager.orders.invoice.label.region')} />
                    <Form.Input name="region" disabled={isSubmitting} onChange={v => onChangeForm('region', v.target.value)} value={form.region} />
                </Form.Field>
                <Form.Field>
                    <Form.Label label={t('ticket.manager.orders.invoice.label.information')} />
                    <Form.Input name="information" disabled={isSubmitting} onChange={v => onChangeForm('information', v.target.value)} value={form.information} />
                </Form.Field>
                <Button.Group className={Style['button-group-edit']}>
                    <Button primary disabled={isSubmitting} isLoading={isSubmitting} onClick={() => onSubmit(form)} label={t('ticket.common.action.save')} />
                    <Button disabled={isSubmitting} onClick={() => onCancel()} label={t('ticket.common.action.cancel')} />
                </Button.Group>
            </Form>
        </>
    );
}

FormBilling.propTypes = {
    form: PropTypes.object,
    isSubmitting: PropTypes.bool,
    onChangeForm: PropTypes.func,
    onSubmit: PropTypes.func,
    onCancel: PropTypes.func
};

function RowBilling({ fieldName = 'unknown', value = '' }) {
    const { t } = useTranslation();

    return (
        <tr>
            <td className={Style['td-field']}>
                <InfoTextTooltip title={t(`ticket.manager.orders.invoice.label.${fieldName}`)} />
            </td>
            <td className={Style['td-answer']}>
                <InfoTextTooltip title={value} />
            </td>
        </tr>
    );
}

RowBilling.propTypes = {
    fieldName: PropTypes.string,
    value: PropTypes.string
};

function TableBilling({ data = {} }) {
    return (
        <table className={Style['table']}>
            {Object.entries(data).map(([key, value]) => {
                if (['id', 'url'].includes(key)) {
                    return null;
                }
                return <RowBilling key={key} fieldName={key} value={value} />;
            })}
        </table>
    );
}

TableBilling.propTypes = {
    data: PropTypes.object
};

export default function Billing({ order = null, onSubmit = () => {}, ...rest }) {
    const { t } = useTranslation();
    const { track } = useSegment();
    const toast = React.useContext(ToastContext);
    const [{ event, organization }] = useAppContext();

    const [editMode, setEditMode] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const initialForm = {
        company: null,
        vat_number: null,
        address: null,
        zip_code: null,
        city: null,
        country: null,
        region: null,
        information: null
    };

    const [formInvoice, setForm, setFormObject] = useForm(order?.invoice ? { ...initialForm, ...order.invoice } : { ...initialForm });

    const onCancel = React.useCallback(() => {
        setEditMode(false);
        setFormObject(order?.invoice ? { ...initialForm, ...order.invoice } : { ...initialForm });
        track('ACTION > CANCEL', { 'Page Title': 'ORDER DETAILS' });
    }, [initialForm, setFormObject, order]);

    const handleSubmit = React.useCallback(() => {
        setIsSubmitting(true);
        let invoice = new InvoiceModel(formInvoice);

        invoice
            .save({ organizationId: organization.pk, eventId: event.pk, orderId: order.id_transaction })
            .then(() => {
                toast.success(t('ticket.manager.orders.invoice.toast.saveSuccess'));
                setEditMode(false);
                setIsSubmitting(false);
                onSubmit();
            })
            .catch(e => {
                console.error(e);
                Sentry.captureException(e);
                toast.error(t('ticket.manager.orders.invoice.toast.saveError'));
                setIsSubmitting(false);
            });
        track('ACTION > SAVE', { 'Page Title': 'ORDER DETAILS' });
    }, [order, formInvoice, toast, onSubmit]);

    if (!order) {
        return null;
    }

    return (
        <Card {...rest}>
            <Card.Header>
                <BillingHeader
                    title={t('ticket.common.billing')}
                    subtitle={t('ticket.manager.orders.invoice.subtitle')}
                    onEdit={() => {
                        setEditMode(true);
                        track('ACTION > EDIT BILLING INFORMATION', { 'Page Title': 'ORDER DETAILS' });
                    }}
                    displayEdit={Boolean(!editMode && order.invoice)}
                />
            </Card.Header>
            <Card.Content>
                {!order.invoice && !editMode && (
                    <Card.EmptyState
                        icon={<Icon name="list" />}
                        title={t('ticket.manager.orders.invoice.emptyState.title')}
                        subtitle={t('ticket.manager.orders.invoice.emptyState.subtitle')}
                        labelButton={t('ticket.manager.orders.invoice.emptyState.labelButton')}
                        onClick={() => {
                            setEditMode(true);
                            track('ACTION > ADD BILLING INFORMATION', { 'Page Title': 'ORDER DETAILS' });
                        }}
                    />
                )}
                {order.invoice && !editMode && <TableBilling data={formInvoice} />}
                {editMode && <FormBilling form={formInvoice} onChangeForm={setForm} isSubmitting={isSubmitting} onSubmit={handleSubmit} onCancel={onCancel} />}
            </Card.Content>
        </Card>
    );
}

Billing.propTypes = {
    order: PropTypes.object,
    onSubmit: PropTypes.func
};
